import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import PasswordInput from '../components/ui/password-input';
import PasswordStrengthMeter from '../components/ui/password-strength-meter';
import { Flower, CheckCircle, WarningTriangle } from 'iconoir-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AcceptInvitePage() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const navigate = useNavigate();
  const { checkAuth } = useAuth();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  if (!token) {
    return (
      <div className="min-h-screen bg-page flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center" data-testid="accept-invite-error">
          <WarningTriangle className="w-10 h-10 text-terracotta mx-auto mb-4" strokeWidth={1.5} />
          <h1 className="font-serif text-3xl text-charcoal font-light">Invite link is missing or broken</h1>
          <p className="text-muted mt-3">Ask your admin to send a fresh invite.</p>
          <Button onClick={() => navigate('/')} className="mt-6 btn-secondary" data-testid="accept-invite-home">Go home</Button>
        </div>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      toast.error("Passwords don't match");
      return;
    }
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/accept-invite`, { token, password }, { withCredentials: true });
      await checkAuth();
      setDone(true);
      toast.success('Welcome to Shaadi Planner ✨');
      setTimeout(() => navigate('/dashboard'), 1200);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to accept invite');
    } finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-page flex items-center justify-center px-4" data-testid="accept-invite-done">
        <div className="text-center">
          <CheckCircle className="w-10 h-10 text-moss mx-auto mb-4" strokeWidth={1.5} />
          <h1 className="font-serif text-3xl text-charcoal font-light">You're in!</h1>
          <p className="text-muted mt-2">Taking you to your dashboard…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-page flex items-center justify-center px-4">
      <div className="max-w-md w-full card-surface p-8 sm:p-10" data-testid="accept-invite-page">
        <div className="text-center mb-8">
          <Flower className="w-8 h-8 text-terracotta mx-auto mb-3" strokeWidth={1.5} />
          <p className="text-[10px] tracking-[0.2em] uppercase font-mono text-muted mb-1">You're invited</p>
          <h1 className="font-serif text-3xl text-charcoal font-light">Set your password</h1>
          <p className="text-muted text-sm mt-2">Pick a password to finish setting up your Shaadi Planner account.</p>
        </div>

        <form onSubmit={submit} className="space-y-4" data-testid="accept-invite-form">
          <div>
            <label className="block text-[10px] tracking-widest uppercase font-mono text-muted mb-2">Password (≥8 chars)</label>
            <PasswordInput
              required minLength="8" value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Your new password"
              className="w-full border border-border px-3 py-3 bg-white text-sm focus:outline-none focus:border-terracotta h-auto rounded-none shadow-none"
              data-testid="accept-invite-password"
            />
            <PasswordStrengthMeter password={password} data-testid="accept-invite-password-strength" />
          </div>
          <div>
            <label className="block text-[10px] tracking-widest uppercase font-mono text-muted mb-2">Confirm password</label>
            <PasswordInput
              required minLength="8" value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="Type it again"
              className="w-full border border-border px-3 py-3 bg-white text-sm focus:outline-none focus:border-terracotta h-auto rounded-none shadow-none"
              data-testid="accept-invite-confirm"
            />
          </div>
          <Button type="submit" disabled={submitting}
            className="w-full bg-terracotta text-white hover:bg-terracotta-dark"
            data-testid="accept-invite-submit">
            {submitting ? 'Setting up…' : 'Accept invitation'}
          </Button>
        </form>
      </div>
    </div>
  );
}
